import React, { useState, useRef, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthProvider'
import { useUserRole } from '../../context/UserRoleProvider'
import { supabase } from '../../lib/supabase'
import { invalidateCache } from '../../lib/api'
import { LogOut, User, ChevronDown, Menu, X } from 'lucide-react'
import { Button } from '../ui/button'
import ProfileModal from '../ProfileModal'
import AnnouncementBell from '../announcements/AnnouncementBell'

export default function AppLayout() {
  const { user, loading: authLoading } = useAuth()
  const { isParishAdmin, isSuperAdmin, loading: roleLoading } = useUserRole()
  const navigate = useNavigate()
  const location = useLocation()
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  const [isNavMenuOpen, setIsNavMenuOpen] = useState(false)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
  const profileMenuRef = useRef(null)

  const handleLogout = async () => {
    invalidateCache('/api/v1/auth/me', '/api/v1/users')
    await supabase.auth.signOut()
    navigate('/')
  }

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false)
      }
    }

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isProfileMenuOpen])

  // Close nav menu on route change
  useEffect(() => {
    setIsNavMenuOpen(false)
  }, [location.pathname])

  if (authLoading || (roleLoading && !user)) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center">
        <div className="text-charcoal-muted">Loading...</div>
      </div>
    )
  }

  const isParishRoute = location.pathname.includes('/app/parish/')
  const isDonorRoute = location.pathname.includes('/app/donor/')
  const isAdminRoute = location.pathname.includes('/app/admin/')

  const showAdminNav = isSuperAdmin && isAdminRoute
  const showParishNav = isParishAdmin && isParishRoute && !isSuperAdmin
  const showDonorNav = (!isParishAdmin && !isSuperAdmin) || (isDonorRoute && !isAdminRoute) || (isSuperAdmin && isDonorRoute)

  // Build nav items based on current role/route
  const navItems = []
  if (showAdminNav) {
    navItems.push(
      { label: 'Dashboard', path: '/app/admin/dashboard', active: location.pathname.includes('/app/admin/dashboard') },
      { label: 'Users', path: '/app/admin/users', active: location.pathname.includes('/app/admin/users') },
      { label: 'Parishes', path: '/app/admin/parishes', active: location.pathname.includes('/app/admin/parishes') },
      { label: 'Reports', path: '/app/admin/reports', active: location.pathname.includes('/app/admin/reports') },
      { label: 'Refunds', path: '/app/admin/refunds', active: location.pathname.includes('/app/admin/refunds') },
    )
  }
  if (showDonorNav) {
    navItems.push(
      { label: 'Parish', path: '/app/donor/parish', active: location.pathname.includes('/app/donor/parish') },
      { label: 'Donations', path: '/app/donor/donations', active: location.pathname.includes('/app/donor/donations') },
      { label: 'Round-Ups', path: '/app/donor/roundups', active: location.pathname.includes('/app/donor/roundups') },
      { label: 'Settings', path: '/app/donor/settings', active: location.pathname.includes('/app/donor/settings') },
    )
  }
  if (showParishNav) {
    navItems.push(
      { label: 'Profile', path: '/app/parish/profile', active: location.pathname.includes('/app/parish/profile') },
      { label: 'Events', path: '/app/parish/events', active: location.pathname.includes('/app/parish/events') },
      { label: 'Donations', path: '/app/parish/donations', active: location.pathname.includes('/app/parish/donations') },
      { label: 'Bank Account', path: '/app/parish/bank', active: location.pathname.includes('/app/parish/bank') },
      {
        label: 'Settings',
        path: '/app/parish/settings',
        // Groups, Team and Donation Fields are collapsed into Settings.
        // Groups' own drill-down (parish/groups/:groupId) stays at its
        // original URL rather than moving under /settings, so it's matched
        // here explicitly to keep the nav highlight from vanishing when an
        // admin clicks into a group.
        active:
          location.pathname.includes('/app/parish/settings') ||
          location.pathname.startsWith('/app/parish/groups/'),
      },
    )
  }

  const desktopNavItemClass = (active) =>
    `px-3 py-2 text-sm font-medium transition-colors duration-150 ${
      active
        ? 'text-gold border-b-2 border-gold'
        : 'text-charcoal-muted hover:text-charcoal'
    }`

  const mobileNavItemClass = (active) =>
    `block w-full text-left px-4 py-3 text-sm font-medium border-b border-surface-border transition-colors duration-150 ${
      active
        ? 'text-gold bg-gold/5'
        : 'text-charcoal hover:text-gold hover:bg-gold/5'
    }`

  return (
    <div className="min-h-screen bg-background-light">
      {/* Navigation */}
      <nav className="fixed top-0 z-50 w-full border-b border-surface-border bg-white">
        <div className="w-full px-2 sm:px-4 lg:px-6">
          <div className="flex justify-between items-center h-16">
            {/* Left: Logo + desktop inline nav */}
            <div className="flex items-center space-x-8">
              <Button variant="ghost" onClick={() => navigate('/')}>
                <span className="text-xl font-bold font-display text-charcoal hover:text-gold transition-colors duration-150">
                  Claritas
                </span>
              </Button>

              {/* Desktop inline nav */}
              <div className="hidden md:flex space-x-4">
                {navItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={desktopNavItemClass(item.active)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: bell + profile dropdown + mobile hamburger */}
            <div className="flex items-center gap-1">
              {showDonorNav && <AnnouncementBell />}

              {/* Profile Menu */}
              <div className="relative" ref={profileMenuRef}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setIsProfileMenuOpen((o) => !o); setIsNavMenuOpen(false) }}
                  className="flex items-center space-x-2"
                >
                  <User className="w-4 h-4" />
                  <ChevronDown className={`w-4 h-4 transition-transform duration-150 ${isProfileMenuOpen ? 'rotate-180' : ''}`} />
                </Button>

                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded bg-white border border-surface-border shadow-subtle z-50">
                    <div className="py-1">
                      <div className="px-4 py-2 border-b border-surface-border">
                        <p className="text-sm text-charcoal-subtle">{user?.email}</p>
                      </div>

                      {isSuperAdmin && (
                        <button
                          onClick={() => {
                            navigate(isAdminRoute ? '/app/donor/parish' : '/app/admin/dashboard')
                            setIsProfileMenuOpen(false)
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-charcoal hover:bg-surface-linen transition-colors duration-150"
                        >
                          {isAdminRoute ? 'Switch to Donor View' : 'Switch to Admin View'}
                        </button>
                      )}

                      {isParishAdmin && !isSuperAdmin && (
                        <button
                          onClick={() => {
                            navigate(isParishRoute ? '/app/donor/parish' : '/app/parish/profile')
                            setIsProfileMenuOpen(false)
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-charcoal hover:bg-surface-linen transition-colors duration-150"
                        >
                          {isParishRoute ? 'Switch to Donor View' : 'Switch to Parish Admin View'}
                        </button>
                      )}

                      <button
                        onClick={() => { setIsProfileModalOpen(true); setIsProfileMenuOpen(false) }}
                        className="w-full text-left px-4 py-2 text-sm text-charcoal hover:bg-surface-linen transition-colors duration-150 flex items-center space-x-2"
                      >
                        <User className="w-4 h-4" />
                        <span>Edit Profile</span>
                      </button>

                      <button
                        onClick={() => { handleLogout(); setIsProfileMenuOpen(false) }}
                        className="w-full text-left px-4 py-2 text-sm text-charcoal hover:bg-surface-linen transition-colors duration-150 flex items-center space-x-2"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Log out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile-only hamburger */}
              <button
                onClick={() => { setIsNavMenuOpen((o) => !o); setIsProfileMenuOpen(false) }}
                className="md:hidden p-2 rounded text-charcoal-muted hover:text-charcoal hover:bg-surface-border/40 transition-colors duration-150 focus:outline-none"
                aria-label="Toggle navigation"
              >
                {isNavMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile nav dropdown */}
        {isNavMenuOpen && (
          <div className="md:hidden border-t border-surface-border bg-white">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={mobileNavItemClass(item.active)}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        {/* TODO: Add suspended parish check when parish data is available in layout */}
        <Outlet />
      </main>

      {/* Profile Modal */}
      {isProfileModalOpen && (
        <ProfileModal onClose={() => setIsProfileModalOpen(false)} />
      )}
    </div>
  )
}
